import React from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { AlertCircle, Inbox, ChevronRight, ChevronLeft } from 'lucide-react';

// רכיבי תצוגה משותפים לעמודי פורטל הסוכן — טבלה קומפקטית עם גלילה אופקית במובייל,
// סרגל סינון, עימוד, ומצבי ריק/שגיאה.

export const PortalPage = styled.div`
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
  direction: rtl;
  background: white;
  min-height: 100vh;

  @media (max-width: 767px) {
    padding: 16px;
  }
`;

export const PageHeader = styled.div`
  margin-bottom: 20px;

  @media (max-width: 1023px) {
    padding-left: 56px; /* לא לעלות על כפתור התפריט הצף (שמאל-למעלה) */
  }
`;

export const PageTitle = styled.h1`
  font-size: 26px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;

  @media (max-width: 767px) {
    font-size: 22px;
  }
`;

export const PageSubtitle = styled.p`
  margin: 6px 0 0;
  color: var(--text-secondary);
  font-size: 14px;
`;

/** כותרת עמוד עם פעולה ראשית בצד השמאלי (נשברת לשורה נפרדת במסך צר). */
export const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
`;

// ----- Buttons -----

const buttonBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 9px 18px;
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  font-weight: 600;
  font-family: inherit;
  text-decoration: none;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s, border-color 0.15s;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid var(--primary-color);
    outline-offset: 2px;
  }
`;

const primaryStyles = css`
  ${buttonBase}
  background: var(--primary-color);
  border: 1px solid var(--primary-color);
  color: #fff;

  &:hover:not(:disabled) {
    background: var(--primary-hover);
    border-color: var(--primary-hover);
  }
`;

export const PrimaryButton = styled.button`
  ${primaryStyles}
`;

/** קישור בעיצוב כפתור ראשי — לניווט (למשל "ליד חדש"). */
export const PrimaryLink = styled(Link)`
  ${primaryStyles}
`;

export const SecondaryButton = styled.button`
  ${buttonBase}
  background: var(--surface-color);
  border: 1px solid var(--border-color);
  color: var(--text-primary);

  &:hover:not(:disabled) {
    border-color: var(--primary-color);
    color: var(--primary-color);
  }
`;

export const Panel = styled.section`
  background: var(--surface-color);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  overflow: hidden;
`;

export const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--border-color);
`;

export const PanelTitle = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const TableWrap = styled.div`
  overflow-x: auto;
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
`;

export const Th = styled.th`
  padding: 10px 12px;
  text-align: right;
  font-weight: 600;
  font-size: 13px;
  color: var(--text-secondary);
  background: var(--background-color);
  border-bottom: 1px solid var(--border-color);
  white-space: nowrap;
`;

export const Td = styled.td`
  padding: 10px 12px;
  text-align: right;
  color: var(--text-primary);
  border-bottom: 1px solid var(--border-color-light);
  vertical-align: middle;
  white-space: nowrap;
`;

export const Tr = styled.tr<{ $clickable?: boolean }>`
  height: 44px;

  ${p => p.$clickable && `
    cursor: pointer;
    &:hover { background: var(--hover-background); }
    &:focus-visible { outline: 2px solid var(--primary-color); outline-offset: -2px; }
  `}

  &:last-child td {
    border-bottom: none;
  }
`;

export const Muted = styled.span`
  color: var(--text-tertiary);
`;

export const Money = styled.span<{ $tone?: 'default' | 'positive' | 'negative' }>`
  direction: ltr;
  display: inline-block;
  font-weight: ${p => (p.$tone && p.$tone !== 'default' ? 600 : 400)};
  color: ${p =>
    p.$tone === 'positive' ? 'var(--success-color)' : p.$tone === 'negative' ? 'var(--error-color)' : 'inherit'};
`;

// ----- Toolbar -----

export const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid var(--border-color);
`;

export const SearchInput = styled.input`
  width: 260px;
  max-width: 100%;
  padding: 8px 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }

  @media (max-width: 767px) {
    width: 100%;
  }
`;

export const Select = styled.select`
  padding: 8px 10px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  font-family: inherit;
  background: var(--surface-color);
  min-width: 150px;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

export const LinkButton = styled.button`
  background: none;
  border: none;
  padding: 4px 6px;
  color: var(--primary-color);
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

export const LoadingHint = styled.span`
  font-size: 13px;
  color: var(--text-tertiary);
`;

// ----- Tabs -----

export const Tabs = styled.div`
  display: flex;
  gap: 4px;
  border-bottom: 1px solid var(--border-color);
  padding: 0 8px;
`;

export const Tab = styled.button<{ $active: boolean }>`
  background: none;
  border: none;
  border-bottom: 3px solid ${p => (p.$active ? 'var(--primary-color)' : 'transparent')};
  padding: 12px 14px 9px;
  font-size: 15px;
  font-weight: ${p => (p.$active ? 700 : 500)};
  font-family: inherit;
  color: ${p => (p.$active ? 'var(--primary-color)' : 'var(--text-secondary)')};
  cursor: pointer;

  &:hover {
    color: var(--primary-color);
  }
`;

// ----- States -----

const StateBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 40px 16px;
  text-align: center;
  color: var(--text-secondary);
  font-size: 14px;
`;

const RetryButton = styled.button`
  padding: 6px 14px;
  border: 1px solid var(--primary-color);
  border-radius: var(--border-radius-sm);
  background: var(--surface-color);
  color: var(--primary-color);
  font-family: inherit;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: var(--primary-light);
  }
`;

export const EmptyState: React.FC<{ text: string }> = ({ text }) => (
  <StateBox>
    <Inbox size={28} color="var(--text-tertiary)" />
    {text}
  </StateBox>
);

export const ErrorState: React.FC<{ text: string; onRetry: () => void }> = ({ text, onRetry }) => (
  <StateBox role="alert">
    <AlertCircle size={28} color="var(--error-color)" />
    {text}
    <RetryButton type="button" onClick={onRetry}>
      נסה שוב
    </RetryButton>
  </StateBox>
);

export const LoadingState: React.FC = () => <StateBox>טוען...</StateBox>;

// ----- Pager -----

export const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];
export const DEFAULT_PAGE_SIZE = 50;

const PagerBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 10px 16px;
  border-top: 1px solid var(--border-color);
  font-size: 13px;
  color: var(--text-secondary);
`;

const PagerControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PagerButton = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 6px 8px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  background: var(--surface-color);
  color: var(--text-primary);
  cursor: pointer;

  &:hover:not(:disabled) {
    background: var(--hover-background);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const PageSizeSelect = styled.select`
  padding: 4px 6px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  font-family: inherit;
  font-size: 13px;
`;

interface PagerProps {
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

export const Pager: React.FC<PagerProps> = ({ page, pageSize, totalCount, onPageChange, onPageSizeChange }) => {
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const from = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, totalCount);

  return (
    <PagerBar>
      <span>
        {from}-{to} מתוך {totalCount}
      </span>
      <PagerControls>
        <label>
          שורות בעמוד{' '}
          <PageSizeSelect value={pageSize} onChange={e => onPageSizeChange(Number(e.target.value))}>
            {PAGE_SIZE_OPTIONS.map(size => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </PageSizeSelect>
        </label>
        <PagerButton type="button" aria-label="עמוד קודם" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
          <ChevronRight size={16} />
        </PagerButton>
        <span>
          עמוד {page} מתוך {totalPages}
        </span>
        <PagerButton
          type="button"
          aria-label="עמוד הבא"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
        >
          <ChevronLeft size={16} />
        </PagerButton>
      </PagerControls>
    </PagerBar>
  );
};
